import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Divider, Popconfirm, Spin, Table } from "antd";

import ApiMcp from "../../api/mcp";
import useNotification from "../../components/notification/notification";
import { SESSION_ANONYMOUS, SESSION_UNKNOWN, selectSessionStatus } from "../../store/slices/user";
import { clearReturnTo, rememberReturnTo } from "../../common/utils/return-to";
import "./styles.css";

const LOGO_SRC = `${
  process.env.REACT_APP_DEV_URL || document.location.origin
}/api/images/logo.svg`;

// The MCP endpoint is on the same host as the portal, under /api.
const SERVER_URL = `${process.env.REACT_APP_DEV_URL || document.location.origin}/api/mcp`;

const utc = (value) => (value ? new Date(value).toISOString().slice(0, 16).replace("T", " ") : null);

/**
 * LF360-2100 — the user's own AI connections, for the prototype: the address
 * to paste into Claude or ChatGPT, what is connected, and Revoke.
 *
 * A connection is started from the assistant, not from here; this page only
 * gives the address and takes connections away. Revoking stops the
 * assistant's very next call.
 */
function AiConnections() {
  const { t } = useTranslation();
  const location = useLocation();
  const sessionStatus = useSelector(selectSessionStatus);
  const { openNotification } = useNotification();

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [calls, setCalls] = useState({ calls: [], page: 0, hasMore: false, loading: false });

  const signedIn = sessionStatus !== SESSION_UNKNOWN && sessionStatus !== SESSION_ANONYMOUS;

  const load = () => {
    setLoading(true);
    return ApiMcp.getConnections()
      .then((res) => setConnections(res.data.connections || []))
      .catch(() => openNotification({ type: "error", message: <p>{t("ai_connections.failed")}</p> }))
      .finally(() => setLoading(false));
  };

  // The user's own calls, 30 at a time, newest first.
  const loadCalls = (page) => {
    setCalls((current) => ({ ...current, loading: true }));
    return ApiMcp.getCalls({ page })
      .then((res) => setCalls((current) => ({
        calls: page === 1 ? res.data.calls : [...current.calls, ...res.data.calls],
        page,
        hasMore: res.data.hasMore,
        loading: false,
      })))
      .catch(() => {
        setCalls((current) => ({ ...current, loading: false }));
        openNotification({ type: "error", message: <p>{t("ai_connections.failed")}</p> });
      });
  };

  // Once per sign-in. `openNotification` is a new function on every render, so
  // depending on it would reload the list forever.
  useEffect(() => {
    if (!signedIn) return;
    clearReturnTo();
    load();
    loadCalls(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  if (sessionStatus === SESSION_UNKNOWN) {
    return <Spin fullscreen />;
  }

  if (sessionStatus === SESSION_ANONYMOUS) {
    rememberReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  const copy = () => {
    navigator.clipboard?.writeText(SERVER_URL).then(() => setCopied(true)).catch(() => {});
  };

  const revoke = (connection) => ApiMcp.revokeConnection(connection.id)
    .then(() => {
      openNotification({
        type: "success",
        message: <p>{t("ai_connections.revoked", { client: connection.client.name || connection.client.host })}</p>,
      });
      return load();
    })
    .catch(() => openNotification({ type: "error", message: <p>{t("ai_connections.failed")}</p> }));

  const columns = [
    {
      title: t("ai_connections.client"),
      key: "client",
      render: (_, row) => (
        <>
          <div>{row.client.name || row.client.host}</div>
          <div className="oauth-consent__muted">{row.client.host}</div>
        </>
      ),
    },
    { title: t("ai_connections.project"), key: "project", render: (_, row) => row.project.title || row.project.id },
    { title: t("ai_connections.scopes"), key: "scopes", render: (_, row) => row.scopes.join(", ") },
    { title: t("ai_connections.created"), key: "createdAt", render: (_, row) => utc(row.createdAt) },
    {
      title: t("ai_connections.last_used"),
      key: "lastUsedAt",
      render: (_, row) => utc(row.lastUsedAt) || t("ai_connections.never"),
    },
    { title: t("ai_connections.expires"), key: "expiresAt", render: (_, row) => utc(row.expiresAt) },
    {
      title: "",
      key: "actions",
      render: (_, row) => (
        <Popconfirm
          title={t("ai_connections.revoke_title", {
            client: row.client.name || row.client.host,
            project: row.project.title || row.project.id,
          })}
          description={t("ai_connections.revoke_text")}
          okText={t("ai_connections.revoke")}
          okButtonProps={{ danger: true }}
          onConfirm={() => revoke(row)}
        >
          <Button danger>{t("ai_connections.revoke")}</Button>
        </Popconfirm>
      ),
    },
  ];

  const callColumns = [
    { title: t("ai_connections.call_time"), key: "createdAt", render: (_, row) => utc(row.createdAt) },
    { title: t("ai_connections.client"), key: "client", render: (_, row) => row.client.name || row.client.host },
    { title: t("ai_connections.call_tool"), key: "tool", dataIndex: "tool" },
    {
      title: t("ai_connections.call_outcome"),
      key: "outcome",
      render: (_, row) => t(`ai_connections.outcomes.${row.outcome}`, { defaultValue: row.outcome }),
    },
    {
      title: t("ai_connections.call_records"),
      key: "resultCount",
      render: (_, row) => (row.resultCount === null || row.resultCount === undefined ? "" : row.resultCount),
    },
  ];

  return (
    <div className="oauth-consent">
      <div className="oauth-consent__container oauth-consent__container--wide">
        <div className="oauth-consent__header">
          <img className="oauth-consent__logo" src={LOGO_SRC} alt="LifeFit360" />
        </div>
        <Divider />
        <div className="oauth-consent__body">
          <h1 className="oauth-consent__title">{t("ai_connections.title")}</h1>

          <p className="oauth-consent__label">{t("ai_connections.server_url")}</p>
          <div className="oauth-consent__server-url">
            <code data-testid="ai-connections-server-url">{SERVER_URL}</code>
            <Button size="small" onClick={copy}>
              {copied ? t("ai_connections.copied") : t("ai_connections.copy")}
            </Button>
          </div>
          <p className="oauth-consent__muted">{t("ai_connections.setup_claude")}</p>
          <p className="oauth-consent__muted">{t("ai_connections.setup_chatgpt")}</p>

          <Divider />
          <Table
            rowKey="id"
            loading={loading}
            dataSource={connections}
            columns={columns}
            pagination={false}
            locale={{ emptyText: t("ai_connections.empty") }}
          />

          <Divider />
          <h2 className="oauth-consent__label">{t("ai_connections.calls_title")}</h2>
          <p className="oauth-consent__muted">{t("ai_connections.calls_note")}</p>
          <Table
            rowKey="id"
            loading={calls.loading}
            dataSource={calls.calls}
            columns={callColumns}
            pagination={false}
            locale={{ emptyText: t("ai_connections.calls_empty") }}
          />
          {calls.hasMore && (
            <Button className="oauth-consent__more" onClick={() => loadCalls(calls.page + 1)} loading={calls.loading}>
              {t("ai_connections.calls_more")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AiConnections;
